import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import { 
  MessageSquare, 
  User, 
  Map, 
  Award, 
  Plus,
  ArrowRight,
  TrendingUp,
  BookOpen,
  Pen,
  CheckCircle,
  Package,
  Target,
  Flag,
  Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import OnboardingModal from '../../components/OnboardingModal';
import { IdentityActionDock } from '../../components/dashboard/IdentityActionDock';
import { IdentitySessionModal } from '../../components/modals/IdentitySessionModal';
import { DailyReflectionModal } from '../../components/modals/DailyReflectionModal';
import { KnowledgeStoreModal } from '../../components/modals/KnowledgeStoreModal';
import { IdentityFormationWidget } from '../../components/dashboard/IdentityFormationWidget';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function PersonalDashboard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trainingMaturity, setTrainingMaturity] = useState({ style: null, knowledge: null });
  const [installedSkills, setInstalledSkills] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [lifemapData, setLifemapData] = useState(null);
  
  // Identity Action States
  const [showIdentitySession, setShowIdentitySession] = useState(false);
  const [showReflection, setShowReflection] = useState(false);
  const [showKnowledge, setShowKnowledge] = useState(false);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  
  // One-time subtle indicator - appears ONCE after first completed identity session
  // Then disappears forever. No progress, no achievement, just reassurance.
  const [showSeedReady, setShowSeedReady] = useState(false);

  useEffect(() => {
    checkOnboardingStatus();
    checkActiveIdentitySession();
    checkFirstSessionCompletion();
  }, []);

  const checkFirstSessionCompletion = async () => {
    // Check if user completed their first identity session AND hasn't seen this message
    const seenKey = 'humatwin_seed_ready_shown';
    if (localStorage.getItem(seenKey)) return; // Already shown once, never show again
    
    try {
      const res = await apiClient.get('/identity-session/history/all?limit=1');
      const hasCompletedSession = res.data?.some(s => s.status === 'completed' && s.identity_seed_ready);
      
      if (hasCompletedSession) {
        setShowSeedReady(true);
        localStorage.setItem(seenKey, 'true'); // Mark as shown - never show again
        
        // Auto-dismiss after 8 seconds
        setTimeout(() => setShowSeedReady(false), 8000);
      }
    } catch (error) {
      // Silent fail - this is optional reassurance
    }
  };

  const checkActiveIdentitySession = async () => {
    try {
      const res = await apiClient.get('/identity-session/active/current');
      setHasActiveSession(res.data.has_active_session);
    } catch (error) {
      console.error('Failed to check identity session:', error);
    }
  };

  const checkOnboardingStatus = async () => {
    try {
      const response = await apiClient.get('/personal/onboarding-status');
      if (!response.data.onboardingCompleted) {
        setShowOnboarding(true);
      } else {
        fetchData();
      }
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
      toast.error('Unable to check onboarding status');
      fetchData(); // Proceed to fetch data anyway
    } finally {
      setCheckingOnboarding(false);
    }
  };

  const fetchData = async () => {
    try {
      const [threadsRes, maturityRes, skillsRes, lifemapRes] = await Promise.all([
        apiClient.get('/ai/threads?scope=PERSONAL'),
        apiClient.get('/training/maturity'),
        apiClient.get('/skills/my-skills?filter=installed'),
        apiClient.get('/personal/lifemap/dashboard').catch(() => ({ data: null }))
      ]);
      
      setThreads(threadsRes.data.slice(0, 3));
      setTrainingMaturity(maturityRes.data);
      setInstalledSkills(skillsRes.data);
      setLifemapData(lifemapRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      'None': 'bg-gray-100 text-gray-700',
      'Early': 'bg-yellow-100 text-yellow-700',
      'Developing': 'bg-blue-100 text-blue-700',
      'Strong': 'bg-green-100 text-green-700',
      'Mature': 'bg-purple-100 text-purple-700'
    };
    return colors[level] || colors['None'];
  };

  const getTimeframeLabel = (timeframe) => {
    const labels = {
      'short': '0-3 months',
      'medium': '3-12 months',
      'long': '1+ years'
    };
    return labels[timeframe] || timeframe;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'career': 'bg-blue-100 text-blue-700',
      'personal': 'bg-purple-100 text-purple-700',
      'health': 'bg-green-100 text-green-700',
      'education': 'bg-yellow-100 text-yellow-700',
      'relationship': 'bg-pink-100 text-pink-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  // Show onboarding modal if not completed
  if (showOnboarding) {
    return <OnboardingModal />;
  }

  // Show loading state while checking onboarding
  if (checkingOnboarding) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8" data-testid="personal-dashboard" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={isRTL ? 'text-right' : ''}>
        <h1 className="font-heading text-4xl font-bold text-gray-900">
          {t('dashboard.overview')}
        </h1>
        <p className="text-lg text-gray-600 mt-2">
          {t('dashboard.overviewSubtitle')}
        </p>
      </div>

      {/* One-time subtle reassurance - appears ONCE after first identity session */}
      {/* Not progress, not achievement - just gentle reassurance. Disappears forever after. */}
      {showSeedReady && (
        <div 
          className="text-center py-3 text-sm text-gray-500 animate-in fade-in duration-1000"
          onClick={() => setShowSeedReady(false)}
        >
          {t('dashboard.seedReady', 'الآن يمكن للتوأم أن يبدأ بالتشكّل معك بمرور الوقت.')}
        </div>
      )}

      {/* Identity Action Dock - The 3 primary buttons for twin formation */}
      <IdentityActionDock
        onIdentitySessionClick={() => setShowIdentitySession(true)}
        onReflectionClick={() => setShowReflection(true)}
        onKnowledgeClick={() => setShowKnowledge(true)}
        hasActiveSession={hasActiveSession}
      />

      {/* Identity Session Modal */}
      <IdentitySessionModal
        open={showIdentitySession}
        onOpenChange={(open) => {
          setShowIdentitySession(open);
          if (!open) checkActiveIdentitySession();
        }}
      />

      {/* Daily Reflection Modal */}
      <DailyReflectionModal
        open={showReflection}
        onOpenChange={setShowReflection}
      />

      {/* Knowledge Store Modal */}
      <KnowledgeStoreModal
        open={showKnowledge}
        onOpenChange={setShowKnowledge}
      />

      {/* Identity Formation Widget - Shows twin formation status */}
      <IdentityFormationWidget />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Widget 1: Training Status */}
        <Card className="col-span-full p-6 rounded-2xl border-2 border-purple-100" data-testid="training-status-widget">
          <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-2 bg-purple-100 rounded-lg">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-gray-900">{t('dashboard.trainingStatus')}</h3>
            </div>
            <Link to="/personal/twin/train">
              <Button variant="outline" size="sm" className={isRTL ? 'flex-row-reverse' : ''}>
                {t('dashboard.goToTraining')}
                <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-200 space-y-3">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-2 w-full" />
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 space-y-3">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-2 w-full" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Style Training */}
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
              <div className={`flex items-center gap-2 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Pen className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-gray-900">{t('training.styleTraining')}</h4>
              </div>
              {trainingMaturity.style ? (
                <>
                  <div className={`flex items-center justify-between mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Badge className={getLevelColor(trainingMaturity.style.level)}>
                      {trainingMaturity.style.level}
                    </Badge>
                    <span className="text-sm text-gray-600">{trainingMaturity.style.itemCount} {t('dashboard.items')}</span>
                  </div>
                  <div className="h-2 bg-white rounded-full overflow-hidden mb-2">
                    <div 
                      className="h-full bg-purple-600 transition-all" 
                      style={{ width: `${trainingMaturity.style.maturity}%` }}
                    />
                  </div>
                  <p className={`text-xs text-gray-600 ${isRTL ? 'text-right' : ''}`}>{trainingMaturity.style.maturity}% {t('dashboard.mature')}</p>
                </>
              ) : (
                <p className="text-sm text-gray-500">{t('dashboard.loading')}</p>
              )}
            </div>

            {/* Knowledge Training */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className={`flex items-center gap-2 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <BookOpen className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900">{t('training.knowledgeTraining')}</h4>
              </div>
              {trainingMaturity.knowledge ? (
                <>
                  <div className={`flex items-center justify-between mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Badge className={getLevelColor(trainingMaturity.knowledge.level)}>
                      {trainingMaturity.knowledge.level}
                    </Badge>
                    <span className="text-sm text-gray-600">{trainingMaturity.knowledge.itemCount} {t('dashboard.items')}</span>
                  </div>
                  <div className="h-2 bg-white rounded-full overflow-hidden mb-2">
                    <div 
                      className="h-full bg-blue-600 transition-all" 
                      style={{ width: `${trainingMaturity.knowledge.maturity}%` }}
                    />
                  </div>
                  <p className={`text-xs text-gray-600 ${isRTL ? 'text-right' : ''}`}>{trainingMaturity.knowledge.maturity}% {t('dashboard.mature')}</p>
                </>
              ) : (
                <p className="text-sm text-gray-500">{t('dashboard.loading')}</p>
              )}
            </div>
            </div>
          )}
        </Card>

        {/* Widget 1.5: LifeMap Goals & Milestones */}
        <Card className="col-span-full p-6 rounded-2xl border-2 border-amber-100" data-testid="lifemap-widget">
          <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-2 bg-amber-100 rounded-lg">
                <Target className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-gray-900">{t('dashboard.lifeMap')}</h3>
            </div>
            <Link to="/personal/life-map">
              <Button variant="outline" size="sm" className={isRTL ? 'flex-row-reverse' : ''}>
                {t('dashboard.viewLifeMap')}
                <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 space-y-3">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
              <div className="bg-green-50 rounded-xl p-4 border border-green-200 space-y-3">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
          ) : lifemapData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Upcoming Goals */}
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className={`flex items-center gap-2 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Target className="w-5 h-5 text-amber-600" />
                  <h4 className="font-semibold text-gray-900">{t('dashboard.upcomingGoals')}</h4>
                  {lifemapData.stats?.activeGoals > 0 && (
                    <Badge className={`bg-amber-200 text-amber-800 text-xs ${isRTL ? 'mr-auto' : 'ml-auto'}`}>
                      {lifemapData.stats.activeGoals} {t('dashboard.active')}
                    </Badge>
                  )}
                </div>
                
                {lifemapData.upcomingGoals?.length > 0 ? (
                  <div className="space-y-2">
                    {lifemapData.upcomingGoals.map((goal) => (
                      <div key={goal.id} className="p-2 bg-white rounded-lg border border-amber-100">
                        <div className={`flex items-center justify-between mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className={`text-sm font-medium text-gray-900 truncate flex-1 ${isRTL ? 'text-right' : ''}`}>{goal.title}</span>
                          {goal.category && (
                            <Badge className={`${getCategoryColor(goal.category)} text-xs ${isRTL ? 'mr-2' : 'ml-2'}`}>
                              {goal.category}
                            </Badge>
                          )}
                        </div>
                        <div className={`flex items-center gap-2 text-xs text-gray-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          {goal.timeframe && (
                            <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Calendar className="w-3 h-3" />
                              {getTimeframeLabel(goal.timeframe)}
                            </span>
                          )}
                          {goal.progress > 0 && (
                            <span className="text-amber-600 font-medium">{goal.progress}%</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-3">{t('dashboard.noActiveGoals')}</p>
                )}
                
                {lifemapData.stats?.averageProgress > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <div className={`flex items-center justify-between text-xs text-gray-600 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span>{t('dashboard.overallProgress')}</span>
                      <span>{lifemapData.stats.averageProgress}%</span>
                    </div>
                    <div className="h-1.5 bg-white rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-amber-500 transition-all" 
                        style={{ width: `${lifemapData.stats.averageProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Recent Milestones */}
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <div className={`flex items-center gap-2 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Flag className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-gray-900">{t('dashboard.recentMilestones')}</h4>
                  {lifemapData.stats?.totalMilestones > 0 && (
                    <Badge className={`bg-green-200 text-green-800 text-xs ${isRTL ? 'mr-auto' : 'ml-auto'}`}>
                      {lifemapData.stats.totalMilestones} {t('dashboard.total')}
                    </Badge>
                  )}
                </div>
                
                {lifemapData.recentMilestones?.length > 0 ? (
                  <div className="space-y-2">
                    {lifemapData.recentMilestones.map((milestone) => (
                      <div key={milestone.id} className="p-2 bg-white rounded-lg border border-green-100">
                        <div className={`flex items-center justify-between mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className={`text-sm font-medium text-gray-900 truncate flex-1 ${isRTL ? 'text-right' : ''}`}>{milestone.title}</span>
                          {milestone.category && (
                            <Badge className={`${getCategoryColor(milestone.category)} text-xs ${isRTL ? 'mr-2' : 'ml-2'}`}>
                              {milestone.category}
                            </Badge>
                          )}
                        </div>
                        {milestone.date && (
                          <p className={`text-xs text-gray-500 ${isRTL ? 'text-right' : ''}`}>{milestone.date}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-3">{t('dashboard.noMilestones')}</p>
                )}
                
                {lifemapData.stats?.completedGoals > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <div className={`flex items-center gap-2 text-xs text-green-700 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <CheckCircle className="w-4 h-4" />
                      <span>{lifemapData.stats.completedGoals} {t('dashboard.goalsCompleted')}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <Target className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">{t('dashboard.setupGoals')}</p>
              <Link to="/personal/life-map">
                <Button size="sm" className="mt-3">
                  {t('dashboard.getStarted')}
                </Button>
              </Link>
            </div>
          )}
        </Card>

        {/* Widget 2: Enabled Skills */}
        <Card className="p-6 rounded-2xl border-2 border-green-100" data-testid="enabled-skills-widget">
          <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-2 bg-green-100 rounded-lg">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-gray-900">{t('dashboard.enabledSkills')}</h3>
            </div>
            <Link to="/personal/my-skills">
              <Button variant="outline" size="sm" className={isRTL ? 'flex-row-reverse' : ''}>
                {t('mySkills.manage')}
                <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className={`flex items-center justify-between p-3 bg-green-50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="text-sm text-gray-700">{t('dashboard.totalInstalled')}</span>
                <span className="text-2xl font-bold text-green-600">{installedSkills.length}</span>
              </div>

              {installedSkills.length > 0 ? (
                <div className="space-y-2">
                  {installedSkills.slice(0, 4).map((skill) => (
                    <div key={skill.installationId || skill.id} className={`flex items-center justify-between p-2 rounded-lg border border-gray-200 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-2 h-2 rounded-full ${skill.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className="text-sm text-gray-900">{skill.name}</span>
                      </div>
                      <Badge variant="outline" className="text-xs capitalize">{skill.category || 'general'}</Badge>
                    </div>
                  ))}
                  {installedSkills.length > 4 && (
                    <p className="text-xs text-gray-500 text-center">+{installedSkills.length - 4} {t('dashboard.more')}</p>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500 mb-2">{t('mySkills.noSkillsInstalled')}</p>
                  <Link to="/personal/marketplace">
                    <Button variant="outline" size="sm">
                      {t('dashboard.browseSkills')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Existing Quick Actions */}
        <Card className="p-6 rounded-2xl" data-testid="quick-actions-card">
          <h3 className={`font-heading text-lg font-semibold text-gray-900 mb-4 ${isRTL ? 'text-right' : ''}`}>{t('dashboard.quickActions')}</h3>
          <div className="space-y-3">
            <Link to="/personal/assistant">
              <Button
                variant="outline"
                className={`w-full ${isRTL ? 'justify-end flex-row-reverse' : 'justify-start'}`}
                data-testid="new-conversation-button"
              >
                <MessageSquare className={`w-4 h-4 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                {t('assistant.newConversation')}
              </Button>
            </Link>
            <Link to="/personal/marketplace">
              <Button variant="outline" className={`w-full ${isRTL ? 'justify-end flex-row-reverse' : 'justify-start'}`}>
                <Package className={`w-4 h-4 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                {t('dashboard.browseSkills')}
              </Button>
            </Link>
            <Link to="/personal/twin/train">
              <Button variant="outline" className={`w-full ${isRTL ? 'justify-end flex-row-reverse' : 'justify-start'}`}>
                <User className={`w-4 h-4 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                {t('dashboard.trainTwin')}
              </Button>
            </Link>
          </div>
        </Card>

        {/* Existing Recent Conversations */}
        <Card className="p-6 rounded-2xl" data-testid="recent-activity-card">
          <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <h3 className="font-heading text-lg font-semibold text-gray-900">{t('dashboard.recentConversations')}</h3>
            <Link to="/personal/assistant">
              <Button variant="ghost" size="sm">{t('dashboard.viewAll')}</Button>
            </Link>
          </div>
          <div className="space-y-3">
            {threads.length > 0 ? (
              threads.map((thread) => (
                <Link
                  key={thread.id}
                  to={`/personal/assistant/threads/${thread.id}`}
                  className="block p-3 rounded-lg border border-gray-200 hover:border-primary/50 hover:bg-gray-50 transition-all"
                >
                  <p className={`text-sm font-medium text-gray-900 truncate ${isRTL ? 'text-right' : ''}`}>{thread.title}</p>
                  <p className={`text-xs text-gray-500 mt-1 ${isRTL ? 'text-right' : ''}`}>
                    {new Date(thread.updatedAt).toLocaleDateString()}
                  </p>
                </Link>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">{t('dashboard.noConversations')}</p>
                <Link to="/personal/assistant">
                  <Button size="sm" className="mt-3">
                    {t('dashboard.startChatting')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
